'use strict';

const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:userContext');
const callbackQueuePromise = require('../../lib/callbackQueuePromise');
const clientConfigPromise = callbackQueuePromise('phxConfigQueue');

/**
 * PhoenixUserContext
 *
 * @description
 * A wrapping tag that expresses the contents of a template (expects
 * a singular 'script' tag as a child and will emit the contents of
 * that template after replacing the following strings:
 *
 * * ${ACCOUNT_URL}
 * * ${AVATAR_URL}
 * * ${LOGIN_URL}
 * * ${LOGOUT_URL}
 * * ${REGISTRATION_URL}
 *
 * Each of these tokens will be replaced by a suitable URL that is
 * provided via the TempestIdentity global. Once the TempestIdentity user
 * context is known, a 'is-authenticated' class will be applied to this
 * component. If the user is not authenticated, an 'is-unauthenticated'
 * class is applied to this component.
 *
 * @example
 *   <phoenix-user-context>
 *     <script type="text/template">...</script>
 *   </phoenix-user-context>
 */
class PhoenixUserContext extends HTMLElement {
    connectedCallback() {
        window.TempestIdentityPromise.then(() => {
            this.init();
        });
    }

    init() {
        function addBetaFlag(url) {
            if (url.indexOf('?') > -1) {
                url += '&';
            } else {
                url += '?';
            }
            return url + 'membership-beta=true';
        }

        this.accountUrl = window.TempestIdentity.getAccountPageUrl();
        this.loginUrl = window.TempestIdentity.getLoginUrl();
        this.registrationUrl = window.TempestIdentity.getRegistrationUrl();
        this.subscribeUrl = window.TempestIdentity.getSubscribeUrl();
        this.logoutUrl = window.TempestIdentity.getNonInteractiveLogoutUrl();

        this.userProfile = undefined;
        this.isAuthenticated = false;

        // Append membership-beta flag to Tempest Identity URLs when the
        // feature is enabled.
        clientConfigPromise.then((config) => {
            if (config.features.isEnabled('membership-beta')) {
                this.subscribePageLogin = true;
                this.accountUrl = addBetaFlag(this.accountUrl);
                this.loginUrl = addBetaFlag(this.loginUrl);
                this.registrationUrl = addBetaFlag(this.registrationUrl);
                this.subscribeUrl = addBetaFlag(this.subscribeUrl);
                this.logoutUrl = addBetaFlag(this.logoutUrl);
            } else {
                this.subscribePageLogin = config.membership.subscribePageLogin;
            }
            this.customSubscribeUrl = config.membership.customSubscribeUrl;

            window.TempestIdentity.getUserProfile((err, profile) => {
                if (!err) {
                    if (profile) {
                        this.isAuthenticated = true;
                        this.userProfile = profile;
                    }
                }

                this.render();
            });
        });
    }

    render() {
        let templateEl = this.querySelector('script[type="text/template"]');
        if (templateEl) {
            let template = templateEl.textContent;
            const avatarKey = 'AVATAR_URL';
            let vars = {
                ACCOUNT_URL: this.accountUrl,
                LOGIN_URL: this.subscribePageLogin ? this.subscribeUrl : this.loginUrl,
                LOGOUT_URL: this.logoutUrl,
                REGISTRATION_URL:
                    this.customSubscribeUrl ||
                    (this.subscribePageLogin ? this.subscribeUrl : this.registrationUrl),
            };

            if (this.isAuthenticated) {
                vars[avatarKey] = this.userProfile.avatar;
                this.classList.add('is-authenticated');
            } else {
                vars[avatarKey] = '';
                this.classList.add('is-unauthenticated');
            }

            Object.keys(vars).forEach((key) => {
                template = template.replace(new RegExp('\\${\\s*' + key + '\\s*}', 'g'), vars[key]);
            });
            this.innerHTML = template;
        }
    }
}

window.customElements.define('phoenix-user-context', PhoenixUserContext);
measure.end();
